import {gql} from '@apollo/client';
import {useCallback, useMemo} from 'react';

import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';

import {RoutinesQuery_routines_RoutineNode as GqlRoutine} from './__generated/RoutinesQuery';
import {
  EventTriggerConfig,
  extractPromptFromBlocks,
  gqlBlocksToMessageBlocks,
  Routine,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';

export const ROUTINES_QUERY = gql`
  query RoutinesQuery @engComponent(name: LLM) @skipAudit {
    routines {
      id
      name
      description
      messageBlocks {
        ... on DisplayTextBlockNode {
          text
          displayType
        }
        ... on DisplayFileBlockNode {
          externalFileId
          filename
          mediaType
        }
      }
      agentType
      triggerType
      triggerConfig
      status
      allowedApprovalCategories
      workspaceFolder {
        ... on FolderNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      runAs
      memoryEnabled
      createdAt
      modifiedAt
      creator {
        id
        name
        handle
        avatarUrl
      }
      archiveData {
        archived
      }
      permissions {
        appendable
        archivable
        writable
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// TODO(nweeden): Replace hand-written query types with generated types once triggerConfig
// is modeled as a GraphQL union (instead of JSONScalar) in the SDL.
interface RoutinesQueryData {
  routines: Array<
    Omit<GqlRoutine, 'triggerConfig'> & {
      triggerConfig: Record<string, unknown>;
    }
  >;
}

interface UseRoutinesResult {
  routines: Array<Routine>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
}

export default function useRoutines({enabled = true}: {enabled?: boolean} = {}): UseRoutinesResult {
  const queryResult = useSkippableBenchlingQuery<RoutinesQueryData>(ROUTINES_QUERY, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    // Keep serving the current list while a refetch (e.g. after pausing an agent) is in flight, so
    // listings don't swap to a loading state and lose their scroll position.
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  const data = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;

  const routines: Array<Routine> = useMemo(() => {
    if (data == null) {
      return [];
    }
    // The server returns routines in no guaranteed order, and updating a routine (e.g. pausing it)
    // can move it within that order. Sort by creation time so rows stay put across refetches.
    const sortedRoutines = [...data.routines].sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
    );
    return sortedRoutines.map((sc) => {
      const messageBlocks = gqlBlocksToMessageBlocks(sc.messageBlocks);
      return {
        id: sc.id,
        name: sc.name ?? null,
        description: sc.description ?? null,
        prompt: extractPromptFromBlocks(messageBlocks),
        agentType: sc.agentType,
        messageBlocks,
        triggerType: sc.triggerType,
        triggerConfig:
          sc.triggerType === TriggerType.TIME_BASED
            ? (sc.triggerConfig as unknown as TimeTriggerConfig)
            : (sc.triggerConfig as unknown as EventTriggerConfig),
        status: sc.status,
        allowedApprovalCategories: sc.allowedApprovalCategories,
        agentWorkspaceFolderId:
          sc.workspaceFolder != null ? getAccessibleOrInaccessibleId(sc.workspaceFolder) : null,
        runAs: sc.runAs,
        memoryEnabled: sc.memoryEnabled,
        createdAt: sc.createdAt,
        modifiedAt: sc.modifiedAt,
        nextRunAt: null,
        archived: sc.archiveData.archived,
        permissions: {
          appendable: sc.permissions.appendable,
          archivable: sc.permissions.archivable ?? false,
          writable: sc.permissions.writable,
        },
        creatorId: sc.creator?.id ?? '',
        creator: sc.creator ?? null,
      };
    });
  }, [data]);

  const isLoading = !enabled ? false : queryResult.type === QueryResultType.PENDING;
  const isError = queryResult.type === QueryResultType.ERROR;

  const refetch = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  return {
    routines,
    isLoading,
    isError,
    refetch,
  };
}
